/* eslint-disable local/require-isPending-for-mutations */
import {
  MediaObjectUpload,
  SignedUploadField,
  UploadMediaParams,
  uploadMedia,
} from '@kudos/http-client'
import { setErrorsFromApiError } from '@shared/errors/setErrorsFromApiError'
import { useApiError } from '@shared/errors/useApiError'
import {
  getZeroSizeValidationMsg,
  isFileSizeGreaterThanZero,
} from '@shared/uploads/ImageUploads.util'
import { useMutation } from '@tanstack/react-query'
import { useT } from '@transifex/react'
import { isAxiosError } from 'axios'
import { useState } from 'react'
import type { FieldValues, Path, UseFormClearErrors, UseFormSetError } from 'react-hook-form'

export type AttachmentType = 'image' | 'document' | 'gif'

export type Attachment = {
  mediaObjectId: string
  file?: File | null
  attachmentType: AttachmentType
}

export type InitiateUploadResponseParams = {
  initiateUploadResponse: MediaObjectUpload
  file: File
}

export type FileMediaUploadParams = {
  mutationFn?: (params: {
    originalFilename: string
    contentType: string
  }) => Promise<MediaObjectUpload>
  /** When set, API validation errors (e.g. path `file`) are applied via setErrorsFromApiError. */
  setFormError?: UseFormSetError<FieldValues>
  clearFormErrors?: UseFormClearErrors<FieldValues>
  /** Whitelist for setErrorsFromApiError; use `[]` so API path `file` is treated as a root form error. */
  apiErrorFieldPaths?: Path<FieldValues>[]
}
export const useFileMediaUpload = ({
  mutationFn,
  setFormError,
  clearFormErrors,
  apiErrorFieldPaths = [],
}: FileMediaUploadParams) => {
  const t = useT()
  const { handleApiError } = useApiError()

  // locally generated object url for the uploaded resource
  const [attachmentSrc, setAttachmentSrc] = useState<string | undefined>()

  // the media object id that is returned after upload
  const [mediaObjectId, setMediaObjectId] = useState<string>()

  const [isUploading, setIsUploading] = useState<boolean>(false)
  /** Set when the upload flow failed, to block the uploader useEffect from retrying in a loop. */
  const [error, setInternalUploadError] = useState<Error | undefined>()

  const initiateUploadMutation = useMutation({
    mutationFn,
  })

  const completeUploadMutation = useMutation({
    mutationFn: ({ url, data }: UploadMediaParams) => uploadMedia({ url, data }),
  })

  const initiateUpload = async (file: File) => {
    if (!isFileSizeGreaterThanZero(file)) {
      const message = getZeroSizeValidationMsg(t)
      if (setFormError) {
        setFormError('root', { type: 'custom', message })
      } else {
        handleApiError(new Error(message))
      }
      setInternalUploadError(new Error(''))
      return undefined
    }
    if (clearFormErrors) {
      clearFormErrors('root')
    }
    setIsUploading(true)
    try {
      // request the upload from backend
      if (initiateUploadMutation.isPending || completeUploadMutation.isPending) {
        return
      }
      const initiateUploadResponse = await initiateUploadMutation.mutateAsync({
        originalFilename: file.name,
        contentType: file.type,
      })

      // parse out signed upload url and aws requirements
      const { awsFormData, mediaObjectId, uploadUrl } = handleInitiateUploadResponse({
        initiateUploadResponse,
        file,
      })

      // complete the upload
      await completeUploadMutation.mutateAsync({
        url: uploadUrl,
        data: awsFormData,
      })

      // set final media object before clearing uploading flag
      setMediaObjectId(mediaObjectId)
      setIsUploading(false)
      return mediaObjectId
    } catch (error) {
      if (isAxiosError(error) && setFormError) {
        setErrorsFromApiError(error, setFormError, apiErrorFieldPaths)
      } else if (setFormError) {
        setFormError('root', {
          type: 'custom',
          message: error instanceof Error ? error.message : 'Unknown error',
        })
      } else {
        handleApiError(new Error('Unknown error'))
      }
      // Truthy value blocks the upload useEffect from retrying; message is on the form (or toast if no setFormError)
      setInternalUploadError(new Error(''))
      setIsUploading(false)
    }
  }

  const handleInitiateUploadResponse = ({
    initiateUploadResponse,
    file,
  }: InitiateUploadResponseParams) => {
    const uploadFields = initiateUploadResponse.upload.fields
    const mediaObjectId = initiateUploadResponse.mediaObject.id
    const uploadUrl = new URL(initiateUploadResponse.upload.url).href
    const awsFormData = new FormData() // 'multipart/form-data' constructor

    uploadFields.forEach(([key, value]: SignedUploadField) => {
      awsFormData.append(key, value)
    })
    awsFormData.append('file', file)

    return { awsFormData, mediaObjectId, uploadUrl }
  }

  const createObjectUrl = (file?: File) => {
    if (!!file) {
      // render the image locally in the component while
      // it uploads
      const srcUrl = URL.createObjectURL(file)
      setAttachmentSrc(srcUrl)
    } else if (!!attachmentSrc) {
      // revoke the generated object url for the file
      URL.revokeObjectURL(attachmentSrc)
      setAttachmentSrc('')
    }
  }

  return {
    createObjectUrl,
    initiateUpload,
    attachmentSrc,
    mediaObjectId,
    isUploading,
    error,
  }
}
